import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'app/rootReducer'
import { Formik, Field, Form, FormikHelpers, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Button from 'react-bootstrap/Button'
import {
  CreatePasswordValidationType,
  ConfirmPasswordValidationType,
  confirmPasswordValidation,
  createPasswordValidation,
  getPasswordRulesPopover,
  createPasswordValidationTypeInitial,
  confirmPasswordValidationTypeInitial
} from 'share/utils/passwordUtils'
import {
  ResetPasswordVariables,
  ResetPasswordResponseData
} from 'share/mutations/account'

export interface ResetPasswordFormProps {
  placement: 'right' | 'top'
  createPasswordValidationResult: CreatePasswordValidationType
  setCreatePasswordValidationResult: React.Dispatch<
    React.SetStateAction<CreatePasswordValidationType>
  >
  confirmPasswordValidationResult: ConfirmPasswordValidationType
  setConfirmPasswordValidationResult: React.Dispatch<
    React.SetStateAction<ConfirmPasswordValidationType>
  >
  resetPasswordHelper(
    input: ResetPasswordVariables
  ): Promise<ResetPasswordResponseData>
  setToDashboard: React.Dispatch<React.SetStateAction<boolean>>
}

interface Values {
  temporaryPassword: string
  createPassword: string
  confirmPassword: string
}

const validationSchema = Yup.object({
  temporaryPassword: Yup.string().required('Required'),
  createPassword: Yup.string().required('Required'),
  confirmPassword: Yup.string().required('Required')
})

let popover: JSX.Element = <></>
const createPwdInitial = {
  ...createPasswordValidationTypeInitial(),
  isValid: true
}
const confirmPwdInitial = {
  ...confirmPasswordValidationTypeInitial(),
  isValid: true
}

const ResetPasswordForm = (props: ResetPasswordFormProps): JSX.Element => {
  const {
    placement,
    createPasswordValidationResult,
    setCreatePasswordValidationResult,
    confirmPasswordValidationResult,
    setConfirmPasswordValidationResult,
    resetPasswordHelper,
    setToDashboard
  } = props
  const forgotPasswordData = useSelector(
    (state: RootState) => state.forgotPasswordData
  )

  const [popoverValidation, setPopoverValidation] = useState(() =>
    createPasswordValidationTypeInitial()
  )

  const initialValues = {
    temporaryPassword: '',
    createPassword: '',
    confirmPassword: ''
  }

  const handlePasswordInput = (password: string) => {
    setCreatePasswordValidationResult(createPwdInitial)
    const result = createPasswordValidation(password)
    setPopoverValidation(result)
  }

  useEffect(() => {
    popover = getPasswordRulesPopover(popoverValidation)
  }, [popoverValidation])

  const handleSubmitPasswordCheck = (
    password: string,
    confirmPassword: string
  ): [CreatePasswordValidationType, ConfirmPasswordValidationType] => {
    let createPwdResult = createPasswordValidationTypeInitial()
    let confirmPwdResult = confirmPasswordValidationTypeInitial()

    createPwdResult = createPasswordValidation(password)
    if (confirmPassword.length > 0) {
      confirmPwdResult = confirmPasswordValidation(confirmPassword, password)
    } else {
      confirmPwdResult.errorMessage = 'This field is required'
    }

    return [createPwdResult, confirmPwdResult]
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (
        values: Values,
        { setSubmitting }: FormikHelpers<Values>
      ) => {
        const [createPwdResult, confirmPwdResult] = handleSubmitPasswordCheck(
          values.createPassword,
          values.confirmPassword
        )

        if (createPwdResult.isValid && confirmPwdResult.isValid) {
          // call api
          const input: ResetPasswordVariables = {
            code: values.temporaryPassword,
            newPassword: values.createPassword,
            accountId: forgotPasswordData.accountId
          }
          const resetPasswordResult = await resetPasswordHelper(input)
          if (resetPasswordResult.success) {
            setToDashboard(true)
          } else {
            setConfirmPasswordValidationResult({
              ...confirmPwdResult,
              errorMessage: 'Error with the password reset. Please try again.'
            })
          }
        } else {
          setSubmitting(false)
          setCreatePasswordValidationResult(createPwdResult)
          setConfirmPasswordValidationResult({
            ...confirmPwdResult,
            confirmPasswordStyle: 'form-control is-invalid'
          })
        }
      }}
      validationSchema={validationSchema}
    >
      {(formik) => {
        return (
          <Form className='form-container'>
            <div className='row'>
              <div className='form-group col-12 col-sm-12 col-md-12'>
                <label htmlFor='temporaryPassword'>Temporary Password</label>
                <Field
                  type='password'
                  id='temporaryPassword'
                  name='temporaryPassword'
                  className={
                    formik.errors.temporaryPassword
                      ? 'form-control is-invalid'
                      : 'form-control'
                  }
                  placeholder=''
                  autoFocus
                />
                <ErrorMessage
                  name='temporaryPassword'
                  render={(msg) => <div className='text-danger'>{msg}</div>}
                />
              </div>
            </div>

            <div className='row'>
              <div className='form-group col-12 col-sm-12 col-md-12'>
                <label htmlFor='createPassword'>New Password</label>
                <OverlayTrigger
                  trigger='focus'
                  defaultShow={false}
                  placement={placement}
                  overlay={popover}
                >
                  <Field
                    type='password'
                    id='createPassword'
                    name='createPassword'
                    className={
                      formik.errors.createPassword
                        ? 'form-control is-invalid'
                        : createPasswordValidationResult.createPasswordStyle
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.handleChange(e)
                      handlePasswordInput(e.target.value)
                    }}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handlePasswordInput(e.target.value)
                    }}
                  />
                </OverlayTrigger>
                {createPasswordValidationResult.errorMessages.map(
                  (errorMsg) => (
                    <div className='text-danger' key={errorMsg}>
                      {errorMsg}
                    </div>
                  )
                )}
                <ErrorMessage
                  name='createPassword'
                  render={(msg) => <div className='text-danger'>{msg}</div>}
                />
              </div>
            </div>

            <div className='row'>
              <div className='form-group col-12 col-sm-12 col-md-12 mb-0'>
                <label htmlFor='confirmPassword'>Confirm Password</label>
                <Field
                  type='password'
                  id='confirmPassword'
                  name='confirmPassword'
                  className={
                    formik.errors.createPassword
                      ? 'form-control is-invalid'
                      : confirmPasswordValidationResult.confirmPasswordStyle
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e)
                    setConfirmPasswordValidationResult(confirmPwdInitial)
                  }}
                  placeholder=''
                />
                {confirmPasswordValidationResult.errorMessage && (
                  <div className='text-danger'>
                    {confirmPasswordValidationResult.errorMessage}
                  </div>
                )}
                <ErrorMessage
                  name='confirmPassword'
                  render={(msg) => <div className='text-danger'>{msg}</div>}
                />
              </div>
            </div>

            <div className='row'>
              <div className='col-12'>
                <Button type='submit' className='btn w-100 reset-btn'>
                  Reset Password
                </Button>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default ResetPasswordForm
